import { DOMSerializer, Node as ProseMirrorNode, Schema, Mark } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';
import { MarkdownSerializer, MarkdownSerializerState } from 'prosemirror-markdown';

function quote(str: string) {
  // 🎯 UNICODE NATIVE FIX: Escape quotes manually instead of using JSON.stringify
  // JSON.stringify can encode Unicode characters, so we preserve Unicode and escape quotes manually
  return '"' + str.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
}

// Custom serializer for nodes, including table support
const nodeSerializers: { [key: string]: (state: MarkdownSerializerState, node: ProseMirrorNode, parent: ProseMirrorNode, index: number) => void } = {
  blockquote(state, node) {
    state.wrapBlock("> ", null, node, () => state.renderContent(node));
  },
  code_block(state, node) {
    state.write("```" + (node.attrs.params || "") + "\n");
    state.text(node.textContent, false);
    state.ensureNewLine();
    state.write("```");
    state.closeBlock(node);
  },
  heading(state, node) {
    state.write(state.repeat("#", node.attrs.level) + " ");
    state.renderInline(node);
    state.closeBlock(node);
  },
  horizontal_rule(state, node) {
    state.write(node.attrs.markup || "---");
    state.closeBlock(node);
  },
  bullet_list(state, node) {
    state.renderList(node, "  ", () => "* ");
  },
  ordered_list(state, node) {
    const start = node.attrs.order || 1;
    const maxW = String(start + node.childCount - 1).length;
    const space = state.repeat(" ", maxW + 2);
    state.renderList(node, space, (i: number) => {
      const nStr = String(start + i);
      return state.repeat(" ", maxW - nStr.length) + nStr + ". ";
    });
  },
  list_item(state, node) {
    // 🚨 CRITICAL FIX: Add checkbox/task list support
    if (node.attrs && typeof node.attrs.checked === 'boolean') {
      const checkbox = node.attrs.checked ? '[x] ' : '[ ] ';
      state.write(checkbox);
    }
    state.renderContent(node);
  },
  paragraph(state, node) {
    state.renderInline(node);
    state.closeBlock(node);
  },
  image(state, node) {
    state.write("![" + state.esc(node.attrs.alt || "") + "](" + state.esc(node.attrs.src) +
      (node.attrs.title ? " " + quote(node.attrs.title) : "") + ")");
  },
  hard_break(state) {
    state.write("\\\n");
  },
  text(state, node) {
    state.text(node.text ?? '');
  },
  table(state, node) {
    state.renderContent(node);
    state.closeBlock(node);
  },
  table_row(state, node) {
    if (node.child(0).type.name === 'table_header') {
      // Header row
      node.forEach((cell, _, i) => {
        state.write(i === 0 ? "| " : " | ");
        state.renderInline(cell);
      });
      state.write(" |\n");
      // Separator
      node.forEach((_, i) => {
        state.write(i === 0 ? "|---" : "|---");
      });
      state.write("|\n");
    } else {
      // Body row
      node.forEach((cell, _, i) => {
        state.write(i === 0 ? "| " : " | ");
        state.renderInline(cell);
      });
      state.write(" |\n");
    }
  },
  table_header(state, node) {
    state.renderInline(node);
  },
  table_cell(state, node) {
    state.renderInline(node);
  },
};

// Custom serializer for marks
const markSerializers: { [key: string]: any } = {
  em: { open: "*", close: "*", mixable: true, expelEnclosingWhitespace: true },
  strong: { open: "**", close: "**", mixable: true, expelEnclosingWhitespace: true },
  link: {
    open: "[",
    close(state: MarkdownSerializerState, mark: Mark) {
      return "](" + state.esc(mark.attrs.href) + (mark.attrs.title ? " " + quote(mark.attrs.title) : "") + ")";
    }
  },
  code: { open: "`", close: "`", escape: false },
  // 🚨 CRITICAL FIX: Add missing mark serializers that exist in schema
  strike: { open: "~~", close: "~~", mixable: true, expelEnclosingWhitespace: true },
  underline: { open: "<u>", close: "</u>", mixable: true }, // HTML fallback since markdown doesn't have underline
  subscript: { open: "<sub>", close: "</sub>", mixable: true }, // HTML fallback
  superscript: { open: "<sup>", close: "</sup>", mixable: true }, // HTML fallback
  // Handle fontFamily and other styling marks that don't have markdown equivalents
  // Simply ignore them (don't render any markdown formatting)
  fontFamily: { open: "", close: "", mixable: true },
  fontSize: { open: "", close: "", mixable: true },
  fontWeight: { open: "", close: "", mixable: true },
  textColor: { open: "", close: "", mixable: true },
  backgroundColor: { open: "", close: "", mixable: true }
};

export const customMarkdownSerializer = new MarkdownSerializer(nodeSerializers, markSerializers);

// Function to get the document as a JSON object
export const getDocAsJson = (state: EditorState): object => {
  return state.doc.toJSON();
};

// Function to get the document as an HTML string
export const getDocAsHtml = (state: EditorState, schema: Schema): string => {
  const fragment = DOMSerializer.fromSchema(schema).serializeFragment(state.doc.content);
  
  // 🎯 UNICODE NATIVE: Use XMLSerializer instead of innerHTML to preserve Unicode characters
  // innerHTML automatically converts Unicode (—, ", ') to HTML entities (&mdash;, &quot;, &#39;)
  // XMLSerializer preserves the original Unicode characters
  const xmlSerializer = new XMLSerializer();
  return Array.from(fragment.childNodes)
    .map(node => xmlSerializer.serializeToString(node))
    .join('');
};

// Function to get the document as a Markdown string
// DEPRECATED: Use docApi.toMarkdown(ydoc) instead for better mark support and Y.js compatibility
export const getDocAsMarkdown = (state: EditorState): string => {
  try {
    return customMarkdownSerializer.serialize(state.doc);
  } catch (error) {
    console.error("Error during Markdown serialization:", error);
    console.warn("Consider using docApi.toMarkdown() with Y.js document for better compatibility");
    return "Error: Could not serialize document to Markdown. See console for details.";
  }
};
